import { Delete, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import PlusOneIcon from "@mui/icons-material/PlusOne";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { deleteFlowTest, getCaptureFlowTests } from "../../api/flowTests/FlowTest";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { FlowTestResponse } from "../../types/model/flowTest";
import { formatDate } from "../../utils";

interface FlowTestsTableProps {
  captureId: number;
  canEdit: boolean;
}

export default function FlowTestsTable({ captureId, canEdit }: Readonly<FlowTestsTableProps>) {
  const navigate = useNavigate();
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();

  const [flowTests, setFlowTests] = useState<FlowTestResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [flowTestToDelete, setFlowTestToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const response = await getCaptureFlowTests(captureId);
      setFlowTests(response);
    } catch (error) {
      handleError(error)
    } finally {
      setIsLoading(false)
    }
  };

  const handleDeleteClick = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    setFlowTestToDelete(id);
  };

  const handleCancelDelete = () => {
    setFlowTestToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (flowTestToDelete === null) return;

    setIsDeleting(true);
    try {
      await deleteFlowTest(flowTestToDelete);
      refreshData();
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
      setFlowTestToDelete(null);
    }
  };

  useEffect(() => {
    refreshData();
  }, [captureId]);

  const handleRequestAdd = (captureId: number) => {
    navigate(`/captures/${captureId}/flow-tests/add-flow-test`);
  };

  const handleClick = (id: number) => {
    navigate(`/flow-tests/${id}`);
  };

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = flowTests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  let tableBodyContent;

  if (isLoading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={4} align="center">
          <CircularProgress />
        </TableCell>
      </TableRow>
    );
  } else if (visibleRows.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              opacity: 0.6,
            }}
          >
            <SearchOff sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">
              {t("flow-tests.table.no-flow-tests")}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  } else {
    tableBodyContent = visibleRows.map((row) => (
      <TableRow
        hover
        key={row.id}
        onClick={() => handleClick(row.id)}
        sx={{
          cursor: "pointer"
        }}
      >
        <TableCell>
          {row.testTypeName ?? "-"}
        </TableCell>
        <TableCell>
          {row.startDate ? formatDate(row.startDate) : "-"}
        </TableCell>
        <TableCell>
          {row.aquiferName ?? "-"}
        </TableCell>
        <TableCell align="center">
          {canEdit && (
            <Tooltip title={t("generic.delete")}>
              <IconButton
                color="error"
                onClick={(event) => handleDeleteClick(event, row.id)}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("flow-tests.table.title")}
          </Typography>

          {/* Refresh button */}
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={refreshData}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {/* Add flow test button */}
          {canEdit && (
            <Tooltip title={t("flow-tests.table.add-flow-test")}>
              <IconButton onClick={() => handleRequestAdd(captureId)}>
                <PlusOneIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("flow-tests.table.test-type")}</TableCell>
                <TableCell>{t("flow-tests.table.start-date")}</TableCell>
                <TableCell>{t("flow-tests.table.aquifer")}</TableCell>
                <TableCell>{t("generic.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableBodyContent}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={flowTests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={flowTestToDelete !== null} onClose={handleCancelDelete}>
        <DialogTitle>{t("flow-tests.table.delete-confirmation-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("flow-tests.table.delete-confirmation-message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("generic.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}